/**
 * DOM helpers.
 * SECURITY: user-supplied text (names, comments) must only ever be set via
 * `text` / textContent — never innerHTML.
 */

export const $ = (selector, root = document) => root.querySelector(selector);
export const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

/**
 * el('div', { class: 'x', text: 'hi', attrs: { title: 't' }, on: { click } }, [children])
 */
export function el(tag, options = {}, children = []) {
  const node = document.createElement(tag);
  const { class: className, text, attrs, on, dataset } = options;
  if (className) node.className = className;
  if (text !== undefined && text !== null) node.textContent = String(text);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === false || value === null || value === undefined) continue;
      node.setAttribute(key, value === true ? '' : String(value));
    }
  }
  if (dataset) Object.assign(node.dataset, dataset);
  if (on) for (const [event, handler] of Object.entries(on)) node.addEventListener(event, handler);
  for (const child of [].concat(children)) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

/** Up to two initials, grapheme-aware (works for Burmese and emoji names). */
export function initials(name) {
  const clean = String(name ?? '')
    .replace(/[^\p{L}\p{N}\p{M}\s]/gu, '')
    .trim();
  if (!clean) return '★';
  const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
  const firstGrapheme = (word) => (segmenter ? [...segmenter.segment(word)][0]?.segment : word[0]) ?? '';
  const words = clean.split(/\s+/);
  const letters = words.length > 1 ? firstGrapheme(words[0]) + firstGrapheme(words[1]) : firstGrapheme(words[0]);
  return letters.toUpperCase();
}

// TikTok CDN avatars, or a data: avatar uploaded by an older version and still in saved sessions.
const SAFE_IMAGE_SRC = /^(?:https:\/\/|data:image\/(?:png|jpeg|webp);base64,)/i;

export function avatarNode(entry, className = 'avatar') {
  const wrapper = el('span', { class: className, attrs: { 'aria-hidden': 'true' } });
  const fallback = () => {
    wrapper.replaceChildren(document.createTextNode(initials(entry.displayName || entry.username)));
  };
  if (entry.avatar && SAFE_IMAGE_SRC.test(entry.avatar)) {
    const img = el('img', {
      attrs: { src: entry.avatar, alt: '', loading: 'lazy', decoding: 'async', referrerpolicy: 'no-referrer' },
    });
    img.addEventListener('error', fallback, { once: true });
    wrapper.append(img);
  } else {
    fallback();
  }
  return wrapper;
}

const numberFormat = new Intl.NumberFormat();
export const fmt = (n) => numberFormat.format(n);

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = el('a', { attrs: { href: url, download: filename } });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function slug(text, fallback = 'item') {
  const s = String(text ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return s || fallback;
}

export function timestamp(date = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}${p(date.getSeconds())}`;
}

/** 83_500 → "1:23" */
export function clock(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const p = (n) => String(n).padStart(2, '0');
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h ? `${h}:${p(m)}:${p(total % 60)}` : `${m}:${p(total % 60)}`;
}

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const prefersReducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Reads a theme CSS variable (optionally a comma-separated list). */
export function themeVar(name, { list = false } = {}) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return list
    ? value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
    : value;
}

export function announce(message) {
  const region = $('#sr-announcer');
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => (region.textContent = message));
}
